import { useCallback, useMemo, useState } from 'react';

// Use Cases
import {
  getConfigurableMenuItems,
  getMenuItemIdPath,
  setMenuItemAvailable,
  setMenuItemFavorite,
} from './menuManagementUseCase';

/**
 * Draft state of the menu management modal (available / favorite flags).
 * The draft is a copy of the full menu tree, sent as a whole on apply; the modal shows `configurableItems`
 * (draft without forbidden items).
 * @param {object} param0
 * @param {ApiManagerMenuItem[] | undefined} param0.items - Full menu tree from GET /layout
 * @returns {{
 *  isOpen: boolean,
 *  draft: ApiManagerMenuItem[],
 *  configurableItems: ApiManagerMenuItem[],
 *  focusItemId: string | null,
 *  openManagement: (focusItemId?: string | null) => void,
 *  closeManagement: () => void,
 *  toggleAvailable: (itemId: string, available: boolean) => void,
 *  toggleFavorite: (itemId: string, favorite: boolean) => void,
 * }}
 */
const useMenuManagement = ({ items }) => {
  const [draft, setDraft] = useState(/** @type {ApiManagerMenuItem[] | null} */ (null));
  const [focusItemId, setFocusItemId] = useState(/** @type {string | null} */ (null));

  const openManagement = useCallback((/** @type {string | null} */ nextFocusItemId = null) => {
    setDraft(structuredClone(items ?? []));
    setFocusItemId(nextFocusItemId);
  }, [items]);

  const closeManagement = useCallback(() => {
    setDraft(null);
    setFocusItemId(null);
  }, []);

  const toggleAvailable = useCallback((/** @type {string} */ itemId, /** @type {boolean} */ available) => {
    setDraft((current) => (
      current ? setMenuItemAvailable(current, getMenuItemIdPath(current, itemId), available) : current
    ));
  }, []);

  const toggleFavorite = useCallback((/** @type {string} */ itemId, /** @type {boolean} */ favorite) => {
    setDraft((current) => (
      current ? setMenuItemFavorite(current, getMenuItemIdPath(current, itemId), favorite) : current
    ));
  }, []);

  const configurableItems = useMemo(() => getConfigurableMenuItems(draft), [draft]);

  return {
    isOpen: draft !== null,
    draft: draft ?? [],
    configurableItems,
    focusItemId,
    openManagement,
    closeManagement,
    toggleAvailable,
    toggleFavorite,
  };
};

export default useMenuManagement;
